/**
 * 表示形式。docs/09-design-system.md 第6章の数値・日時規則に従う。
 * 保存は UTC、表示は組織のタイムゾーン（既定 Asia/Tokyo）。
 */
export const DEFAULT_TIMEZONE = 'Asia/Tokyo';
const pad = (n) => String(n).padStart(2, '0');
const partsIn = (date, timeZone) => {
    const fmt = new Intl.DateTimeFormat('ja-JP', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const out = {};
    for (const p of fmt.formatToParts(date))
        if (p.type !== 'literal')
            out[p.type] = p.value;
    return {
        year: Number(out.year),
        month: Number(out.month),
        day: Number(out.day),
        // Intl は 24 時を返すことがある
        hour: Number(out.hour) % 24,
        minute: Number(out.minute),
        second: Number(out.second),
    };
};
const toDate = (input) => input instanceof Date ? input : new Date(input);
/** `YYYY/MM/DD HH:mm` */
export const formatDateTime = (input, timeZone = DEFAULT_TIMEZONE) => {
    if (input == null)
        return '—';
    const d = toDate(input);
    if (Number.isNaN(d.getTime()))
        return '—';
    const p = partsIn(d, timeZone);
    return `${p.year}/${pad(p.month)}/${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
};
/** `YYYY/MM/DD` */
export const formatDate = (input, timeZone = DEFAULT_TIMEZONE) => {
    if (input == null)
        return '—';
    const d = toDate(input);
    if (Number.isNaN(d.getTime()))
        return '—';
    const p = partsIn(d, timeZone);
    return `${p.year}/${pad(p.month)}/${pad(p.day)}`;
};
/** `MM/DD` — 一覧の省略表示用 */
export const formatShortDate = (input, timeZone = DEFAULT_TIMEZONE) => {
    if (input == null)
        return '—';
    const d = toDate(input);
    if (Number.isNaN(d.getTime()))
        return '—';
    const p = partsIn(d, timeZone);
    return `${pad(p.month)}/${pad(p.day)}`;
};
/** `HH:mm` */
export const formatTime = (input, timeZone = DEFAULT_TIMEZONE) => {
    if (input == null)
        return '—';
    const d = toDate(input);
    if (Number.isNaN(d.getTime()))
        return '—';
    const p = partsIn(d, timeZone);
    return `${pad(p.hour)}:${pad(p.minute)}`;
};
/** `YYYY-MM-DD`（API のクエリ・集計キー用） */
export const toDateKey = (input, timeZone = DEFAULT_TIMEZONE) => {
    const p = partsIn(toDate(input), timeZone);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};
/** 24 時間以内は相対表記。それ以外は null */
export const formatRelative = (input, now = new Date()) => {
    if (input == null)
        return null;
    const d = toDate(input);
    if (Number.isNaN(d.getTime()))
        return null;
    const diffMs = now.getTime() - d.getTime();
    const abs = Math.abs(diffMs);
    if (abs >= 86_400_000)
        return null;
    const future = diffMs < 0;
    const minutes = Math.floor(abs / 60_000);
    if (minutes < 1)
        return future ? 'まもなく' : 'たった今';
    if (minutes < 60)
        return future ? `${minutes}分後` : `${minutes}分前`;
    const hours = Math.floor(minutes / 60);
    return future ? `${hours}時間後` : `${hours}時間前`;
};
/** 日時＋相対表記（例: `2026/09/06 10:20（3時間前）`） */
export const formatDateTimeWithRelative = (input, timeZone = DEFAULT_TIMEZONE, now = new Date()) => {
    if (input == null)
        return '—';
    const base = formatDateTime(input, timeZone);
    const rel = formatRelative(input, now);
    return rel ? `${base}（${rel}）` : base;
};
/** 3 桁区切り */
export const formatNumber = (n) => n == null || Number.isNaN(n) ? '—' : n.toLocaleString('ja-JP');
/** 率は小数第 1 位まで。分母 0（null）は「—」 */
export const formatRate = (value) => value == null || Number.isNaN(value) ? '—' : `${(value * 100).toFixed(1)}%`;
/** 前期間比のポイント差 */
export const formatRateDelta = (value) => {
    if (value == null || Number.isNaN(value))
        return '—';
    const pt = value * 100;
    const sign = pt > 0 ? '+' : '';
    return `${sign}${pt.toFixed(1)}pt`;
};
export const formatCountDelta = (value) => {
    if (value == null || Number.isNaN(value))
        return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString('ja-JP')}`;
};
export const formatUsd = (value, fractionDigits = 2) => value == null || Number.isNaN(value) ? '—' : `$${value.toFixed(fractionDigits)}`;
export const formatDuration = (ms) => {
    if (ms == null || Number.isNaN(ms))
        return '—';
    if (ms < 1000)
        return `${Math.round(ms)}ms`;
    const sec = ms / 1000;
    if (sec < 60)
        return `${sec.toFixed(1)}秒`;
    const min = Math.floor(sec / 60);
    const rest = Math.round(sec % 60);
    return `${min}分${rest}秒`;
};
/** ログ表示用のマスキング（docs/02-functional-spec.md F11.2） */
export const maskEmail = (email) => {
    const at = email.indexOf('@');
    if (at <= 0)
        return '***';
    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    const head = local.slice(0, 1);
    return `${head}${'*'.repeat(Math.max(local.length - 1, 1))}@${domain}`;
};
export const maskSecret = (value, visibleTail = 4) => {
    if (!value)
        return '';
    if (value.length <= visibleTail)
        return '*'.repeat(value.length);
    const prefix = value.slice(0, Math.min(3, value.length - visibleTail));
    return `${prefix}...${value.slice(-visibleTail)}`;
};
/** `tel:` リンク用に数字のみへ */
export const telHref = (phone) => `tel:${phone.replace(/[^0-9+]/g, '')}`;
//# sourceMappingURL=format.js.map